import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import { VBS, priceText, resolvePrice } from "../theme";
import { firstListMediaUrl } from "lib/media";

/** Bordered grid card — catalog */
export function ProductCardGrid({ product, currency, onOpen, onAdd, canOrder }) {
  const { t } = useStorefrontCopy();
  const quoteLabel = t("common.requestQuote", "Request quote");
  const image = firstListMediaUrl(product?.images);
  const unitPrice = resolvePrice(product);
  const hasPrice = Number(unitPrice) > 0;

  return (
    <Box
      onClick={onOpen}
      sx={{
        cursor: "pointer",
        height: "100%",
        bgcolor: VBS.surface,
        border: `1px solid ${VBS.lineStrong}`,
        borderRadius: `${VBS.radiusMd}px`,
        overflow: "hidden",
        boxShadow: VBS.shadowCard,
        transition: "box-shadow 180ms ease, border-color 180ms ease",
        "&:hover": {
          borderColor: VBS.accent,
          boxShadow: VBS.shadowLift,
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          aspectRatio: "4 / 3",
          bgcolor: VBS.imageBg,
          borderBottom: `1px solid ${VBS.line}`,
        }}
      >
        {image ? (
          <Box
            component="img"
            src={image}
            alt={product.name}
            loading="lazy"
            decoding="async"
            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : null}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 3,
            background: VBS.swoosh,
            opacity: 0.9,
          }}
        />
      </Box>
      <Box sx={{ p: 1.75 }}>
        <Box
          component="span"
          sx={{
            fontFamily: VBS.fontMono,
            fontSize: "0.6875rem",
            color: VBS.accent,
            letterSpacing: "0.04em",
            fontWeight: 700,
            textTransform: "uppercase",
          }}
        >
          {product.sku || t("common.skuTbd", "SKU TBD")}
        </Box>
        <Box
          component="h3"
          sx={{
            m: "0.35rem 0 0",
            fontSize: "0.9375rem",
            fontWeight: 700,
            lineHeight: 1.35,
            color: VBS.ink,
          }}
        >
          {product.name}
        </Box>
        <Box
          sx={{
            mt: 1.25,
            pt: 1.25,
            borderTop: `1px solid ${VBS.line}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: "1rem",
              fontWeight: 800,
              color: VBS.accent,
              fontFamily: VBS.fontMono,
            }}
          >
            {priceText(unitPrice, currency, quoteLabel)}
          </Box>
          {hasPrice && canOrder && onAdd ? (
            <Box
              component="button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAdd(product);
              }}
              sx={{
                margin: 0,
                border: `1px solid ${VBS.accent}`,
                borderRadius: `${VBS.radius}px`,
                bgcolor: VBS.accentSoft,
                color: VBS.accent,
                fontWeight: 700,
                fontSize: "0.6875rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                px: 1.25,
                py: 0.5,
                cursor: "pointer",
              }}
            >
              {t("common.add", "Add")}
            </Box>
          ) : (
            <Box component="span" sx={{ fontSize: "0.75rem", color: VBS.subtle }}>
              {t("common.details", "Details →")}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

/** Wide horizontal row — home featured strip */
export function ProductCardRow({ product, currency, onOpen, onAdd, canOrder }) {
  const { t } = useStorefrontCopy();
  const quoteLabel = t("common.requestQuote", "Request quote");
  const image = firstListMediaUrl(product?.images);
  const unitPrice = resolvePrice(product);
  const hasPrice = Number(unitPrice) > 0;

  return (
    <Box
      onClick={onOpen}
      sx={{
        cursor: "pointer",
        minWidth: { xs: 280, sm: 340 },
        maxWidth: 360,
        flexShrink: 0,
        bgcolor: VBS.surface,
        border: `1px solid ${VBS.lineStrong}`,
        borderRadius: `${VBS.radiusMd}px`,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        scrollSnapAlign: "start",
      }}
    >
      <Box sx={{ height: 160, bgcolor: VBS.imageBg, position: "relative" }}>
        {image ? (
          <Box
            component="img"
            src={image}
            alt={product.name}
            loading="lazy"
            decoding="async"
            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : null}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 3,
            background: VBS.swoosh,
            opacity: 0.9,
          }}
        />
      </Box>
      <Box sx={{ p: 1.5, flex: 1, display: "flex", flexDirection: "column" }}>
        <Box
          component="span"
          sx={{
            fontFamily: VBS.fontMono,
            fontSize: "0.625rem",
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: VBS.accent,
          }}
        >
          {product.sku}
        </Box>
        <Box
          component="h3"
          sx={{
            m: "0.25rem 0 0",
            fontSize: "0.875rem",
            fontWeight: 700,
            color: VBS.ink,
            lineHeight: 1.35,
            flex: 1,
          }}
        >
          {product.name}
        </Box>
        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            component="span"
            sx={{ fontWeight: 800, color: VBS.accent, fontFamily: VBS.fontMono }}
          >
            {priceText(unitPrice, currency, quoteLabel)}
          </Box>
          {hasPrice && canOrder && onAdd ? (
            <Box
              component="button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAdd(product);
              }}
              sx={{
                margin: 0,
                border: 0,
                bgcolor: VBS.accent,
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.625rem",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                px: 1,
                py: 0.45,
                borderRadius: `${VBS.radius}px`,
                cursor: "pointer",
              }}
            >
              {t("common.add", "Add")}
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

const shape = PropTypes.shape({
  name: PropTypes.string,
  sku: PropTypes.string,
  images: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
});

ProductCardGrid.propTypes = {
  product: shape.isRequired,
  currency: PropTypes.string,
  onOpen: PropTypes.func.isRequired,
  onAdd: PropTypes.func,
  canOrder: PropTypes.bool,
};

ProductCardGrid.defaultProps = {
  currency: "USD",
  onAdd: null,
  canOrder: false,
};

ProductCardRow.propTypes = ProductCardGrid.propTypes;
ProductCardRow.defaultProps = ProductCardGrid.defaultProps;

export default ProductCardGrid;
